import { Injectable, inject } from '@angular/core'

import type { Observable } from 'rxjs'
import { catchError, map, of, switchMap } from 'rxjs'

import { ApiService } from '../../api/api.service'
import type {
  CancelScheduledChangeResponseDto,
  SubscriptionDto,
} from '../../api/responses'
import { AuthService } from '../auth/auth.service'

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  private apiService = inject(ApiService)
  private authService = inject(AuthService)

  getSubscriptions(
    filterOrganizationId?: number
  ): Observable<SubscriptionDto[]> {
    return this.authService.waitForAuthCheckCompletion().pipe(
      switchMap(() => {
        // If the user is not authenticated return an empty array of subscriptions
        if (!this.authService.currentUser()) return of([])

        return this.getOrganizationSubscriptions(filterOrganizationId)
      })
    )
  }

  getSubscriptionById(id: number): Observable<SubscriptionDto | null> {
    return this.apiService.getSubscriptionWithPricingDetails(id).pipe(
      // Map the API response to extract the updated subscription seats information
      map(response => response.data.Subscription)
    )
  }

  /**
   * Configure subscriptions for an organization by adding new products and/or
   * updating existing subscriptions. This will return a URL for the invoice
   * payment if there are any changes that require payment. If there are no
   * changes or if the changes do not require payment, it will return null.
   *
   * To
   *
   * @param configuration - The configuration object containing the organization
   *   ID, products to add,
   * @returns Observable of the created invoice payment URL
   */
  configureSubscription(configuration: {
    organizationId?: number | null
    reference?: string | null
    productsToAdd?:
      | {
          id: number
          quantity: number
          reference?: string | null
          addImmediately?: boolean
        }[]
      | null
    subscriptionsToUpdate?:
      | {
          id: number
          quantity?: number | null
          reference?: string | null
          cancel?: boolean | null
          updateImmediately?: boolean
        }[]
      | null
  }): Observable<string | null> {
    // Validate current user
    const user = this.authService.currentUser()
    if (!user) throw new Error('No current user found')
    if (!user.organization)
      throw new Error('No organization found for current user')

    if (configuration.productsToAdd) {
      // Filter out product options with zero quantity
      configuration.productsToAdd = configuration.productsToAdd.filter(
        poa => poa.quantity > 0
      )
      if (configuration.productsToAdd.length === 0)
        throw new Error(
          'No product options with quantity greater than zero found'
        )
    }

    // Call API to buy subscriptions
    const ConfigureSubscriptionsResult = this.apiService.configureSubscription(
      configuration.organizationId ?? null,
      configuration.reference ?? null,
      configuration.productsToAdd ?? null,
      configuration.subscriptionsToUpdate ?? null
    )

    // Handle response
    return ConfigureSubscriptionsResult.pipe(
      map(response => {
        // Validate response
        if (!response.data)
          throw new Error('Failed to create subscription: No data returned')

        return response.data.configureSubscriptions.paymentUrl
      })
    )
  }

  public getOrganizationSubscriptionsWithPricingDetails(
    page = 1,
    limit = 10,
    filterOrganizationIds?: Set<number>
  ): Observable<{
    docs: SubscriptionDto[]
    totalDocs: number
    totalPages: number
    page: number
  }> {
    return this.authService.currentUser$.pipe(
      switchMap(user => {
        const organizationId = user?.organization?.id
        if (!organizationId) {
          throw Error('pages.organization.errors.noOrganization')
        }
        return this.apiService.getSubscriptionsWithPricingDetails(
          page,
          limit,
          filterOrganizationIds
        )
      }),
      map(response => {
        const result = response.data.Subscriptions
        return {
          docs: result.docs,
          totalDocs: result.totalDocs,
          totalPages: result.totalPages,
          page: result.page,
        }
      }),
      catchError(() => of({ docs: [], totalDocs: 0, totalPages: 0, page }))
    )
  }

  public requestSeat(magazineId: number): Observable<boolean> {
    return this.apiService.requestSeat(magazineId).pipe(
      map(response => {
        const success = response.data?.requestSeat.success
        if (typeof success !== 'boolean')
          throw new Error('Failed to request seat')

        return success
      })
    )
  }

  cancelScheduledChange(
    scheduledChangeId: number
  ): Observable<CancelScheduledChangeResponseDto> {
    return this.apiService.cancelScheduledChange(scheduledChangeId).pipe(
      map(response => {
        const cancelScheduledChange = response.data
        if (!cancelScheduledChange) {
          throw new Error(
            'Failed to cancel scheduled change: No cancelScheduledChange returned'
          )
        }
        return cancelScheduledChange
      })
    )
  }

  getProrationFactor(organizationId?: number): Observable<number> {
    return this.apiService.getProrationFactor(organizationId).pipe(
      map(result => {
        const factor = result.data?.prorationFactor.factor
        if (factor === undefined) {
          throw new Error('No proration factor returned')
        }
        return factor
      })
    )
  }

  private getOrganizationSubscriptions(
    filterOrganizationId?: number
  ): Observable<SubscriptionDto[]> {
    return this.apiService.getSubscriptions(filterOrganizationId).pipe(
      map(response => response.data.Subscriptions.docs),
      catchError(() => of([]))
    )
  }
}
